import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUserAccounts } from "../../../../stores/User/User.hooks";
import { useCreateSepaMandate } from "../../../../stores/Sepa/sepa.mutations";
import { useSepaBankAccounts } from "../../../../stores/Sepa/sepa.queries";
import { normalizeIban } from "../../../../stores/Sepa/sepa.validation";
import { MandateSequenceTypeEnum, MandateStatusEnum } from "../../../../stores/Sepa/sepa.types";
import BankAccountSelector, { BankSelection, emptyNewBank } from "./BankAccountSelector";
import { bankSelectionToDto, validateBankSelection } from "./bankSelection";

interface Props {
  patientId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optionally preselect the creditor account (e.g. from a bill's account). */
  defaultAccountId?: string;
}

/**
 * Creates a reusable (RCUR) SEPA mandate: pick the creditor account (Mandant) and
 * a bank account (existing or new). One-off charges use the einmal-Lastschrift flow
 * instead, so this dialog is always recurring.
 */
const MandateFormDialog: React.FC<Props> = ({
  patientId,
  open,
  onOpenChange,
  defaultAccountId,
}) => {
  const accounts = useUserAccounts() ?? [];
  const createMandate = useCreateSepaMandate();
  const { data: bankAccounts = [] } = useSepaBankAccounts(patientId);

  const [accountId, setAccountId] = useState<string>(defaultAccountId ?? "");
  const [bank, setBank] = useState<BankSelection>(emptyNewBank);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Preselect the only account, or the provided default, when the dialog opens.
  const resolvedAccountId = useMemo(() => {
    if (accountId) return accountId;
    if (defaultAccountId) return defaultAccountId;
    if (accounts.length === 1) return accounts[0].id;
    return "";
  }, [accountId, defaultAccountId, accounts]);

  // The backend silently returns the existing mandate when one already covers this
  // bank + creditor. Detect that up front so we can tell the user clearly (#12).
  const duplicateMandate = useMemo(() => {
    if (!resolvedAccountId) return null;
    const matchedAccount =
      bank.mode === "existing"
        ? bankAccounts.find((a) => a.id === bank.bankAccountId)
        : bankAccounts.find((a) => normalizeIban(a.iban) === normalizeIban(bank.iban));
    if (!matchedAccount) return null;
    return (
      (matchedAccount.mandates ?? []).find(
        (m) =>
          m.sequenceType === MandateSequenceTypeEnum.Rcur &&
          m.accountId === resolvedAccountId &&
          (m.status === MandateStatusEnum.Active || m.status === MandateStatusEnum.Draft),
      ) ?? null
    );
  }, [bank, bankAccounts, resolvedAccountId]);

  const reset = () => {
    setAccountId("");
    setBank(emptyNewBank);
    setErrors({});
  };

  const handleSubmit = () => {
    const bankErrors = validateBankSelection(bank);
    const nextErrors = { ...bankErrors };
    if (!resolvedAccountId) {
      nextErrors.accountId = "Bitte ein Konto wählen";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    createMandate.mutate(
      {
        patientId,
        accountId: resolvedAccountId,
        sequenceType: MandateSequenceTypeEnum.Rcur,
        ...bankSelectionToDto(bank),
      },
      {
        onSuccess: () => {
          reset();
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl">Neues SEPA-Mandat</DialogTitle>
          <DialogDescription className="text-[15px]">
            Wiederverwendbares Lastschriftmandat (mehrfach nutzbar).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-1.5">
            <Label className="text-base">Konto (Mandant)</Label>
            <Select value={resolvedAccountId} onValueChange={setAccountId}>
              <SelectTrigger className="h-11 text-base">
                <SelectValue placeholder="Konto wählen" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.accountId && <p className="text-sm text-destructive">{errors.accountId}</p>}
          </div>

          <BankAccountSelector
            patientId={patientId}
            value={bank}
            onChange={setBank}
            errors={errors}
            disabled={createMandate.isPending}
          />

          {duplicateMandate && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              Für diese Bankverbindung und diesen Mandanten besteht bereits ein wiederverwendbares
              Mandat
              {duplicateMandate.mandateReference ? (
                <>
                  {" "}
                  (<span className="font-mono text-xs">{duplicateMandate.mandateReference}</span>)
                </>
              ) : null}
              . Es kann kein zweites angelegt werden.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            className="text-base"
            onClick={() => onOpenChange(false)}
            disabled={createMandate.isPending}
          >
            Abbrechen
          </Button>
          <Button
            className="bg-blue-darkest text-base text-white hover:bg-blue-darkest/90"
            onClick={handleSubmit}
            disabled={createMandate.isPending || !!duplicateMandate}
          >
            {createMandate.isPending ? "Wird erstellt..." : "Mandat erstellen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MandateFormDialog;
